import type { ThemeTokenOverrides } from "../types/theme-overrides";

type ThemeTokenObject = Record<string, { value: string }>;

export function mergeTokenObjects(
  base: ThemeTokenObject,
  overrides?: Record<string, string | undefined>
): ThemeTokenObject {
  if (!overrides) return base;

  const cleanOverrides = Object.entries(overrides).reduce<ThemeTokenObject>(
    (acc, [key, value]) => {
      if (!value) return acc;
      acc[key] = { value };
      return acc;
    },
    {}
  );

  return { ...base, ...cleanOverrides };
}

/**
 * Merges radii token overrides while preserving the `full` token.
 * Website theme overrides must not alter the canonical "full" radius
 * to keep circular/round controls (spinner, steppers, icon buttons) stable.
 */
export function mergeRadiiTokenObjects(
  base: ThemeTokenObject,
  overrides?: Record<string, string | undefined>
): ThemeTokenObject {
  const merged = mergeTokenObjects(base, overrides);
  if (base.full) {
    merged.full = base.full;
  }
  return merged;
}

export function buildSemanticColorOverrides(
  overrides?: ThemeTokenOverrides["semanticColors"]
): Record<string, { value: { _light: string; _dark: string } }> {
  if (!overrides) return {};

  return Object.entries(overrides).reduce<
    Record<string, { value: { _light: string; _dark: string } }>
  >((acc, [key, value]) => {
    if (!value) return acc;

    if (typeof value === "string") {
      acc[key] = { value: { _light: value, _dark: value } };
      return acc;
    }

    acc[key] = {
      value: {
        _light: value.light,
        _dark: value.dark,
      },
    };
    return acc;
  }, {});
}
